import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { Trans, useTranslation } from 'react-i18next';
import { gql } from '__generated__';
import { useForm } from 'react-form-state-manager';

import { GetBillingForProjectQuery as GetBillingForProject } from '__generated__/graphql';
import { GetSessionQuery } from '../Auth/SessionProvider';
import { getServerErrors } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import GetBillingForProjectQuery from './GetBillingForProjectQuery';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useNavigationPrompt from '../../common/useNavigationPrompt';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

type Project = GetBillingForProject['project'];

interface BillingPageProps {
  projectId: string;
  refetchQueries?: DocumentNode[];
}

const BillingPage = ({
  projectId, refetchQueries = [GetSessionQuery],
}: BillingPageProps) => {
  const { data } = useQuery(GetBillingForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return (
      <UI.PageLoader />
    );
  }

  return <BillingView project={project} refetchQueries={refetchQueries} />;
};

export const EditServiceFeeMutation = gql(`
  mutation EditServiceFee($input: EditServiceFeeInput!) {
    editServiceFee(input: $input) {
      ...ProjectFields
    }
  }
`);

const editServiceFeeSchema = yup.object().shape({
  pass_on_fee: yup.boolean().required(),
});

const getValues = (project: { pass_on_fee: boolean; }) => ({
  pass_on_fee: project.pass_on_fee,
});

interface BillingViewProps {
  project: Project;
  refetchQueries?: DocumentNode[];
}

const BillingView = ({ project, refetchQueries = [GetSessionQuery] }: BillingViewProps) => {
  const { t } = useTranslation();
  const { formatNumber, formatCurrency } = useLocale();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: getValues(project),
  });

  useNavigationPrompt(form.changed());

  const [editServiceFee, { error, loading }] = useMutation(
    EditServiceFeeMutation, {
      variables: {
        input: {
          ...form.values,
          project: {
            id: project.id,
          },
        },
      },
      refetchQueries,
      onCompleted: ({ editServiceFee: project }) => {
        notifier.success(t('notifications.project_saved'));

        form.reset(getValues(project));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editServiceFeeSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const percentageFeeOnly = project.service_fee_percentage > 0
    && project.minimum_service_fee === 0 && project.maximum_service_fee === 0;

  const percentageFeeWithMinAndMax = project.service_fee_percentage > 0
    && project.minimum_service_fee > 0 && project.minimum_service_fee < project.maximum_service_fee;

  const percentageFeeWithMaxOnly = project.service_fee_percentage > 0
    && project.minimum_service_fee === 0 && project.maximum_service_fee > 0;

  const fixedFee = project.minimum_service_fee > 0
    && project.minimum_service_fee === project.maximum_service_fee;

  const serviceFee = {
    AUD: 136,
    CAD: 123,
    CHF: 83,
    CZK: 2066,
    DKK: 620,
    EUR: 83,
    GBP: 70,
    HUF: 33058,
    NOK: 992,
    PLN: 347,
    SEK: 950,
    USD: 91,
  }[project.currency];

  const passedOnFee = Math.round(serviceFee * 1.21);

  const ticketPrice = 20 * passedOnFee; // i.e. 20 EUR
  const vatMultiplier = (!project.organisation_vat_number || project.organisation_vat_number.startsWith('NL'))
    ? 1.21 : 1;

  const calculatedFee = (() => {
    if (form.values.pass_on_fee) {
      return passedOnFee;
    }

    if (percentageFeeOnly) {
      return Math.round(((ticketPrice * project.service_fee_percentage) / 100) * vatMultiplier);
    }

    if (percentageFeeWithMinAndMax) {
      return Math.round(Math.min(Math.max(
        (ticketPrice * project.service_fee_percentage) / 100,
        project.minimum_service_fee,
      ), project.maximum_service_fee) * vatMultiplier);
    }

    if (percentageFeeWithMaxOnly) {
      return Math.round(Math.min(
        (ticketPrice * project.service_fee_percentage) / 100,
        project.maximum_service_fee,
      ) * vatMultiplier);
    }

    return Math.round(project.minimum_service_fee * vatMultiplier);
  })();

  const participantPays = ticketPrice + (form.values.pass_on_fee ? calculatedFee : 0);
  const organiserFee = form.values.pass_on_fee ? 0 : calculatedFee;
  const organiserRevenue = ticketPrice - organiserFee;

  const soldTicketsCount = Math.max(Math.ceil(project.yearly_registrations_count / 1000) * 1000, 1000);

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H3>
            {t('platform_fee')}
          </UI.H3>
          <UI.InputDescription sc={{ mt: 1 }}>
            {t('platform_fee_description')}
            {(project.pass_on_fee || project.service_fee_percentage > 0) && ` ${t('fee_applies_to_follow_up_payments')}`}
          </UI.InputDescription>
        </UI.Div>

        <UI.Card>
          <UI.Form onSubmit={() => editServiceFee()}>
            <UI.FormGrid>
              <UI.InputGroup sc={{ valid: !errors.pass_on_fee, touched: form.touched.pass_on_fee }}>
                <UI.FormGrid sc={{ gutter: 0.25 }}>
                  <UI.Radio
                    {...form.radio('pass_on_fee', true)}
                    sc={{ box: true }}
                  >
                    <UI.Strong>
                      {t('pass_on_fee')}
                    </UI.Strong>
                    {' '}
                    <UI.Label sc={{
                      background: 'primary.50', color: 'primary.900', fontWeight: 500, small: true,
                    }}
                    >
                      {t('recommended')}
                    </UI.Label>
                    <UI.InputDescription>
                      {t('pass_on_fee_description', { fee: formatCurrency(serviceFee) })}
                    </UI.InputDescription>
                  </UI.Radio>
                  <UI.Radio
                    {...form.radio('pass_on_fee', false)}
                    sc={{ box: true }}
                  >
                    <UI.Strong sc={{ block: true }}>
                      {t('absorb_fee')}
                    </UI.Strong>
                    <UI.InputDescription>
                      {percentageFeeOnly && (
                        <Trans
                          i18nKey="percentage_fee_description"
                          values={{ fee: `${formatNumber(project.service_fee_percentage)}%` }}
                        >
                          <UI.Strong />
                        </Trans>
                      )}
                      {percentageFeeWithMinAndMax && (
                        <Trans
                          i18nKey="percentage_fee_with_min_and_max_description"
                          values={{
                            fee: `${formatNumber(project.service_fee_percentage)}%`,
                            min: formatCurrency(project.minimum_service_fee),
                            max: formatCurrency(project.maximum_service_fee),
                          }}
                        >
                          <UI.Strong />
                        </Trans>
                      )}
                      {percentageFeeWithMaxOnly && project.service_fee_percentage === 15 && (
                        <Trans
                          i18nKey="fixed_fee_description"
                          values={{ fee: formatCurrency(project.maximum_service_fee) }}
                        >
                          <UI.Strong />
                        </Trans>
                      )}
                      {percentageFeeWithMaxOnly && project.service_fee_percentage !== 15 && (
                        <Trans
                          i18nKey="percentage_fee_with_max_only_description"
                          values={{
                            fee: `${formatNumber(project.service_fee_percentage)}%`,
                            max: formatCurrency(project.maximum_service_fee),
                          }}
                        >
                          <UI.Strong />
                        </Trans>
                      )}
                      {fixedFee && (
                        <Trans
                          i18nKey="fixed_fee_description"
                          values={{ fee: formatCurrency(project.minimum_service_fee) }}
                        >
                          <UI.Strong />
                        </Trans>
                      )}
                    </UI.InputDescription>
                  </UI.Radio>
                </UI.FormGrid>
                <UI.ErrorMessages attribute={t('platform_fee')} errors={errors.pass_on_fee} />
              </UI.InputGroup>

              <UI.FadeIn
                sc={{ when: form.getTouched('pass_on_fee') }}
                key={form.values.pass_on_fee ? 'pass_on_fee' : 'absorb_fee'}
              >
                <UI.Info>
                  <UI.H4 sc={{ mb: 2 }}>
                    {form.values.pass_on_fee ? t('example_pass_on_fee') : t('example_absorb_fee')}
                  </UI.H4>

                  <UI.Table sc={{ borderTop: true, borderBottom: true, borderedRows: true }}>
                    <UI.TBody>
                      <UI.TR>
                        <UI.TD style={{ width: '60%' }}>
                          {t('ticket_price')}
                        </UI.TD>
                        <UI.TD sc={{ textAlign: 'right' }}>
                          {formatCurrency(ticketPrice)}
                        </UI.TD>
                      </UI.TR>
                      <UI.TR>
                        <UI.TD>
                          {t(vatMultiplier > 1 ? 'platform_fee_incl_vat' : 'platform_fee')}
                        </UI.TD>
                        <UI.TD sc={{ textAlign: 'right' }}>
                          {formatCurrency(calculatedFee)}
                        </UI.TD>
                      </UI.TR>
                      <UI.TR>
                        <UI.TD>
                          {t('participant_pays')}
                        </UI.TD>
                        <UI.TD sc={{ textAlign: 'right' }}>
                          <UI.FadeIn
                            sc={{ when: form.getTouched('pass_on_fee') }}
                            key={form.values.pass_on_fee ? 'example_pass_on_fee' : 'example_absorb_fee'}
                          >
                            {formatCurrency(participantPays)}
                          </UI.FadeIn>
                        </UI.TD>
                      </UI.TR>
                      <UI.TR sc={{ fontWeight: 500 }}>
                        <UI.TD>
                          {t('organiser_receives')}
                        </UI.TD>
                        <UI.TD sc={{ textAlign: 'right' }}>
                          <UI.FadeIn
                            sc={{ when: form.getTouched('pass_on_fee') }}
                            key={form.values.pass_on_fee ? 'example_pass_on_fee' : 'example_absorb_fee'}
                          >
                            {formatCurrency(organiserRevenue)}
                          </UI.FadeIn>
                        </UI.TD>
                      </UI.TR>
                    </UI.TBody>
                  </UI.Table>

                  <UI.Table sc={{ mt: 2, color: 'gray.500' }}>
                    <UI.TBody>
                      <UI.TR>
                        <UI.TD style={{ width: '60%' }}>
                          {t('expected_costs_organiser', { value: formatNumber(soldTicketsCount) })}
                        </UI.TD>
                        <UI.TD sc={{ textAlign: 'right' }}>
                          <UI.FadeIn
                            sc={{ when: form.getTouched('pass_on_fee') }}
                            key={form.values.pass_on_fee ? 'example_pass_on_fee' : 'example_absorb_fee'}
                          >
                            {formatCurrency(Math.round(organiserFee / vatMultiplier) * soldTicketsCount)}
                          </UI.FadeIn>
                        </UI.TD>
                      </UI.TR>
                    </UI.TBody>
                  </UI.Table>
                </UI.Info>
              </UI.FadeIn>

              {(form.values.pass_on_fee || project.service_fee_percentage === 15) && (
                <UI.Div sc={{ color: 'gray.500', small: true }}>
                  {t('pass_on_fee_footnote')}
                </UI.Div>
              )}

              <UI.HR />

              <UI.FormButtons
                form={form}
                submitLabel={t('save')}
                loading={loading}
                valid={valid}
              />
            </UI.FormGrid>
          </UI.Form>
        </UI.Card>

        <UI.HR />

        <UI.Div>
          <UI.H3>
            {t('invoices')}
          </UI.H3>
        </UI.Div>

        <UI.Card sc={{ small: [true, false] }}>
          <Trans i18nKey="invoices_moved_to">
            <UI.Link to={`/${project.id}/reports/invoices`} />
          </Trans>
        </UI.Card>
      </UI.FormGrid>
    </UI.Container>
  );
};

export default BillingPage;
